import './DateTimeRangePicker.css';

import { useEffect, useRef, useState } from 'react';
import type { FocusEvent } from 'react';
import { Calendar } from 'primereact/calendar';
import { IonIcon } from '@ionic/react';
import { calendarOutline } from 'ionicons/icons';
import { format, parse } from 'date-fns';
import { FormEvent } from 'primereact/ts-helpers';
import { useMask } from '@react-input/mask';
import { Popover } from 'react-tiny-popover';
import { getRangeForTimespan } from '../../../../../utils/filters/getRangeForTimespan';
import DateRangePickerUi from '../../../../DateRangePickerUi/DateRangePickerUi';
import Input from '../../../../Input/Input';
import Button from '../../../../Button/Button';
import {
  DATE_API_FORMAT,
  DATE_INPUT_FORMAT,
  DATE_MASK,
  DATE_MASK_OPTIONS,
  DATE_RANGE_MASK,
  DATE_RANGE_MASK_OPTIONS,
  DATE_TIME_MASK,
  DATE_TIME_MASK_OPTIONS,
  DATE_TIME_RANGE_MASK,
  DATE_TIME_RANGE_MASK_OPTIONS,
  DATETIME_API_FORMAT,
  DATETIME_CHECKBOX_ID,
  DATETIME_INPUT_FORMAT,
  END_OF_DAY_TIME_VALUES,
  START_OF_DAY_TIME_VALUES,
} from '../../../../../constants';
import { DateTimeInputControl } from '../../../../../types/types';
import type { GridInputBaseProps } from '../../types';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { Checkbox } from '../../../../Checkbox';
import { getMinMaxDates } from '../../../../../utils/managerUtils';
import { getApiStringFromDateAndTime } from '../../utils/getApiStringFromDateAndTime';
import { clickedOnDatetimeCheckbox } from '../../utils/clickedOnDatetimeCheckbox';
import { datetimeMaskModify } from '../../utils/datetimeMaskModify';
import { parseInputDateTimeValue } from '../../utils/parseInputDateTimeValue';
import { normalizeDateTimeRange } from './utils/normalizeDateTimeRange';
import { isTargetInsidePickerElements } from './utils/isTargetInsidePickerElements';
import { useMinMaxDateTimeValue } from '../../hooks/useMinMaxDateTimeValue';
import InputErrorMessage from '../../../InputErrorMessage/InputErrorMessage';

type DateTimeRangePickerProps = GridInputBaseProps & {
  control: DateTimeInputControl;
  dateOnly?: boolean;
  isFormInput?: boolean;
};

const DateTimeRangePicker = ({
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  value,
  name,
  error,
  setError,
  disabled,
  isColumnFilter,
  control,
  dateOnly = false,
  isFormInput,
}: DateTimeRangePickerProps) => {
  // In forms time is always allowed and the toggle is hidden; never for date-only fields.
  const forceAllowTime = !!isFormInput && !dateOnly;
  // Emit value to both change and touch in the same moment — non-text controls
  // treat `touch` as an undebounced equivalent of `change`. `propagateHandler`
  // defaults to true; pass false for programmatic writes (mount defaults etc.)
  // to skip firing the field-level handlers in FormHandlersContext.
  const emitChange = (v: string, propagateHandler: boolean = true) => {
    onChange(v, propagateHandler);
    if (propagateHandler) {
      onTouch?.(v);
    }
  };
  const { t } = useTranslate();
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [fromTime, setFromTime] = useState(new Date());
  const [toTime, setToTime] = useState(new Date());
  const [displayedFromDateTime, setDisaplayedFromDateTime] = useState('');
  const [displayedToDateTime, setDisaplayedToDateTime] = useState('');
  const [displayedRangeDateTime, setDisaplayedRangeDateTime] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [allowTimeState, setAllowTime] = useState(false);
  const allowTime = forceAllowTime || allowTimeState;

  const { minValue, maxValue } = useMinMaxDateTimeValue(
    control.min,
    control.max
  );

  const inputFormat = allowTime ? DATETIME_INPUT_FORMAT : DATE_INPUT_FORMAT;
  const apiFormat = allowTime ? DATETIME_API_FORMAT : DATE_API_FORMAT;

  const datetimeRangeMaskRef = useMask({
    ...DATE_TIME_RANGE_MASK_OPTIONS,
    // Prevents mask appearing after clicking the allow time checkbox
    showMask: displayedRangeDateTime !== '',
    // Fixes mask appearing only after entering the second symbol
    modify: (trackingData) =>
      datetimeMaskModify(
        trackingData,
        DATE_TIME_RANGE_MASK_OPTIONS,
        displayedRangeDateTime
      ),
  });
  const dateRangeMaskRef = useMask({
    ...DATE_RANGE_MASK_OPTIONS,
    // Prevents mask appearing after clicking the allow time checkbox
    showMask: displayedRangeDateTime !== '',
    // Fixes mask appearing only after entering the second symbol
    modify: (trackingData) =>
      datetimeMaskModify(
        trackingData,
        DATE_RANGE_MASK_OPTIONS,
        displayedRangeDateTime
      ),
  });
  const datetimeFromMaskRef = useMask(DATE_TIME_MASK_OPTIONS);
  const datetimeToMaskRef = useMask(DATE_TIME_MASK_OPTIONS);
  const dateFromMaskRef = useMask(DATE_MASK_OPTIONS);
  const dateToMaskRef = useMask(DATE_MASK_OPTIONS);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Column-filter mode prefills today's range on first mount; after a user clear we must not refill, so this flips on the initial pass.
  const initialColumnFilterFillDoneRef = useRef(false);

  // True when target is inside input or popover — onFocusIn/Out should not fire.
  const disableCallFocusHandler = (target: EventTarget | null): boolean =>
    isTargetInsidePickerElements(
      target,
      isColumnFilter,
      allowTime,
      datetimeFromMaskRef.current,
      dateFromMaskRef.current,
      datetimeToMaskRef.current,
      dateToMaskRef.current,
      datetimeRangeMaskRef.current,
      dateRangeMaskRef.current,
      popoverRef.current
    );

  const handleFocusIn = (e: FocusEvent) => {
    if (!disableCallFocusHandler(e.target)) {
      onFocusIn?.();
    }
  };

  const handleFocusOut = (e: FocusEvent | MouseEvent) => {
    if (!disableCallFocusHandler(e.target)) {
      onFocusOut?.();
    }
  };

  useEffect(() => {
    let newFromDateTime = new Date();
    newFromDateTime.setHours(...START_OF_DAY_TIME_VALUES);
    let newToDateTime = new Date();
    newToDateTime.setHours(...END_OF_DAY_TIME_VALUES);
    let hasCorrectValue = false;
    let hasTime = false;
    let valueCarriesTime = false;
    if (value) {
      const splitted = value.split(',', 2);
      if (splitted[0] && splitted[1]) {
        const from = parseInputDateTimeValue(splitted[0], dateOnly);
        const to = parseInputDateTimeValue(splitted[1], dateOnly);
        // Accept only a homogeneous range (both time-only or both ISO); mixed/unrecognized clears.
        if (from && to && from.isTimeOnly === to.isTimeOnly) {
          hasCorrectValue = true;
          valueCarriesTime = from.carriesTime && to.carriesTime;
          hasTime = !dateOnly && from.carriesTime && to.carriesTime;
          if (hasTime) {
            setAllowTime(true);
          }
          newFromDateTime = from.date;
          newToDateTime = to.date;
        } else {
          // Unrecognized/mixed range — ignore: show & submit empty.
          emitChange('', false);
          setError(null);
        }
      } else {
        const namedRange = getRangeForTimespan(value);
        if (namedRange.from && namedRange.to) {
          hasCorrectValue = true;
          newFromDateTime = namedRange.from;
          newToDateTime = namedRange.to;
          const apiFormatString =
            hasTime || forceAllowTime ? DATETIME_API_FORMAT : DATE_API_FORMAT;
          const dateTimeString = `${format(newFromDateTime, apiFormatString)},${format(newToDateTime, apiFormatString)}`;
          emitChange(dateTimeString, false);
        } else {
          // Invalid value after switching from another matchmode
          emitChange('', false);
          setError(null);
        }
      }
    }
    const {
      normalizedFromDateTime,
      normalizedToDateTime,
      showTime,
      shouldResetAllowTime,
    } = normalizeDateTimeRange(
      newFromDateTime,
      newToDateTime,
      hasCorrectValue,
      hasTime,
      forceAllowTime,
      minValue,
      maxValue
    );
    newFromDateTime = normalizedFromDateTime;
    newToDateTime = normalizedToDateTime;
    if (shouldResetAllowTime) {
      // Sync checkbox state — reset when value carries no time component
      setAllowTime(false);
    }
    setFromDate(newFromDateTime);
    setFromTime(newFromDateTime);
    setToDate(newToDateTime);
    setToTime(newToDateTime);
    // Column-filter mode prefills today's range only on the very first render. After the user clears the inputs, we keep the display empty instead of refilling the default.
    const shouldColumnFilterPrefill =
      isColumnFilter && !initialColumnFilterFillDoneRef.current && !value;
    if (hasCorrectValue || shouldColumnFilterPrefill) {
      checkMinMaxError(
        newFromDateTime,
        newToDateTime,
        minValue,
        maxValue,
        showTime
      );
      const inputFormatString = showTime
        ? DATETIME_INPUT_FORMAT
        : DATE_INPUT_FORMAT;
      const readableFromDateTimeString = format(
        newFromDateTime,
        inputFormatString
      );
      const readableToDateTimeString = format(newToDateTime, inputFormatString);
      const apiFormatString = showTime ? DATETIME_API_FORMAT : DATE_API_FORMAT;
      const dateTimeString = `${format(newFromDateTime, apiFormatString)},${format(newToDateTime, apiFormatString)}`;
      if (isColumnFilter) {
        setDisaplayedFromDateTime(readableFromDateTimeString);
        setDisaplayedToDateTime(readableToDateTimeString);
        emitChange(dateTimeString, false);
      } else {
        setDisaplayedRangeDateTime(
          `${readableFromDateTimeString} - ${readableToDateTimeString}`
        );
        // Re-emit the normalized range so submit matches the display: a dateTime value (:00 seconds) and a date-only field that received time (times stripped), not the raw range string.
        if (hasTime || (dateOnly && valueCarriesTime)) {
          emitChange(dateTimeString, false);
        }
      }
    } else if (isColumnFilter) {
      // Column filter cleared post-mount — show empty in both range inputs.
      setDisaplayedFromDateTime('');
      setDisaplayedToDateTime('');
    } else {
      // Non-filter reset (filterFirst re-init).
      setDisaplayedRangeDateTime('');
    }
    initialColumnFilterFillDoneRef.current = true;
    //TODO: t in dependencies is a temporary fix for error messages not showing
    // It will probably not be needed after client-side routing merge
    // value dep keeps display in sync with parent updates; column-filter mode prefills today's range only on first mount so a user clear stays empty instead of being refilled.
  }, [t, value]);

  const checkMinMaxError = (
    fromDateTime: Date,
    toDateTime: Date,
    minValue: Date | null,
    maxValue: Date | null,
    allowTime: boolean
  ) => {
    const { min, max } = getMinMaxDates(minValue, maxValue, allowTime);
    const inputFormat = allowTime ? DATETIME_INPUT_FORMAT : DATE_INPUT_FORMAT;
    if (min && (fromDateTime < min || toDateTime < min)) {
      const readableMinValue = format(min, inputFormat);
      setError([t('errors.minValue').replace('{0}', readableMinValue)]);
    } else if (max && (fromDateTime > max || toDateTime > max)) {
      const readableMaxValue = format(max, inputFormat);
      setError([t('errors.maxValue').replace('{0}', readableMaxValue)]);
    }
  };

  const manualFromDateTimeChange = (v) => {
    const mask = allowTime ? DATE_TIME_MASK : DATE_MASK;
    if (v === mask) {
      v = '';
    }
    setDisaplayedFromDateTime(v);
    setError(null);
    const toApiString = getApiStringFromDateAndTime(toDate, toTime, allowTime);
    if (v === '') {
      emitChange(`,${toApiString}`);
    } else {
      const newFromDateTime = parse(v, inputFormat, new Date());
      if (newFromDateTime.getDate()) {
        if (!allowTime) {
          newFromDateTime.setHours(...START_OF_DAY_TIME_VALUES);
        }
        setFromDate(newFromDateTime);
        setFromTime(newFromDateTime);
        emitChange(`${format(newFromDateTime, apiFormat)},${toApiString}`);
      }
    }
  };

  const manualToDateTimeChange = (v) => {
    if (v === DATE_TIME_MASK) {
      v = '';
    }
    setDisaplayedToDateTime(v);
    setError(null);
    const fromApiString = getApiStringFromDateAndTime(
      fromDate,
      fromTime,
      allowTime
    );
    if (v === '') {
      emitChange(`${fromApiString},`);
    } else {
      const newToDateTime = parse(v, inputFormat, new Date());
      if (newToDateTime.getDate()) {
        if (!allowTime) {
          newToDateTime.setHours(...START_OF_DAY_TIME_VALUES);
        }
        setToDate(newToDateTime);
        setToTime(newToDateTime);
        const dateTimeString = `${fromApiString},${format(newToDateTime, apiFormat)}`;
        emitChange(dateTimeString);
      }
    }
  };

  const manualRangeDateTimeChange = (v) => {
    if (v === DATE_TIME_RANGE_MASK || v === DATE_RANGE_MASK) {
      v = '';
    }
    setDisaplayedRangeDateTime(v);
    setError(null);
    if (v === '') {
      emitChange('');
    } else {
      const splitted = v.split(' - ', 2);
      if (splitted[0] && splitted[1]) {
        const newFromDateTime = parse(splitted[0], inputFormat, new Date());
        const newToDateTime = parse(splitted[1], inputFormat, new Date());
        if (
          newFromDateTime.getDate() &&
          newToDateTime.getDate() &&
          newFromDateTime <= newToDateTime
        ) {
          setFromDate(newFromDateTime);
          setFromTime(newFromDateTime);
          setToDate(newToDateTime);
          setToTime(newToDateTime);
          const toApiString = getApiStringFromDateAndTime(
            newToDateTime,
            newToDateTime,
            allowTime
          );
          const dateTimeString = `${format(newFromDateTime, apiFormat)},${toApiString}`;
          emitChange(dateTimeString);
        }
      }
    }
  };

  const setInputAndApiValue = (
    fromDate: Date,
    fromTime: Date,
    toDate: Date,
    toTime: Date,
    allowTime: boolean,
    checkError = false
  ) => {
    setError(null);
    const fromApiString = getApiStringFromDateAndTime(
      fromDate,
      fromTime,
      allowTime
    );
    const toApiString = getApiStringFromDateAndTime(toDate, toTime, allowTime);
    const dateTimeString = `${fromApiString},${toApiString}`;
    emitChange(dateTimeString);
    const dateTimeFrom = new Date(fromApiString);
    const dateTimeTo = new Date(toApiString);
    if (!allowTime) {
      dateTimeFrom.setHours(...START_OF_DAY_TIME_VALUES);
      dateTimeTo.setHours(...START_OF_DAY_TIME_VALUES);
    }
    const inputFormat = allowTime ? DATETIME_INPUT_FORMAT : DATE_INPUT_FORMAT;
    const readableDateTimeFromString = format(dateTimeFrom, inputFormat);
    const readableDateTimeToString = format(dateTimeTo, inputFormat);
    if (isColumnFilter) {
      setDisaplayedFromDateTime(readableDateTimeFromString);
      setDisaplayedToDateTime(readableDateTimeToString);
    } else {
      setDisaplayedRangeDateTime(
        `${readableDateTimeFromString} - ${readableDateTimeToString}`
      );
    }
    if (checkError) {
      checkMinMaxError(dateTimeFrom, dateTimeTo, minValue, maxValue, allowTime);
    }
  };

  const handleRangeSet = (range, namedRangeSet, isPresetClick) => {
    if (range.from && range.to) {
      setFromDate(range.from);
      setToDate(range.to);
      const newFromTime = fromTime;
      const newToTime = toTime;
      let newAllowTime = allowTime;
      if (isPresetClick && !dateOnly && !forceAllowTime) {
        setAllowTime(false);
        newAllowTime = false;
        newFromTime.setHours(...START_OF_DAY_TIME_VALUES);
        newToTime.setHours(...END_OF_DAY_TIME_VALUES);
        setFromTime(newFromTime);
        setToTime(newToTime);
      }
      setInputAndApiValue(
        range.from,
        newFromTime,
        range.to,
        newToTime,
        newAllowTime,
        isColumnFilter
      );
    }
  };

  const onFromTimeChange = (e: FormEvent) => {
    const newFromTime = e.value as Date;
    setFromTime(newFromTime);
    setInputAndApiValue(
      fromDate,
      newFromTime,
      toDate,
      toTime,
      allowTime,
      isColumnFilter
    );
  };

  const onToTimeChange = (e: FormEvent) => {
    const newToTime = e.value as Date;
    setToTime(newToTime);
    setInputAndApiValue(
      fromDate,
      fromTime,
      toDate,
      newToTime,
      allowTime,
      isColumnFilter
    );
  };

  const onSubmit = () => {
    setInputAndApiValue(fromDate, fromTime, toDate, toTime, allowTime, true);
    setPopoverOpen(false);
  };

  const onFromBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (clickedOnDatetimeCheckbox(e)) {
      return;
    }
    const newFromDateTime = parse(
      displayedFromDateTime,
      inputFormat,
      new Date()
    );
    if (newFromDateTime.getDate()) {
      const toApiString = getApiStringFromDateAndTime(
        toDate,
        toTime,
        allowTime
      );
      const toDateTime = new Date(toApiString);
      if (!allowTime) {
        newFromDateTime.setHours(...START_OF_DAY_TIME_VALUES);
        toDateTime.setHours(...START_OF_DAY_TIME_VALUES);
      }

      setError(null);
      checkMinMaxError(
        newFromDateTime,
        toDateTime,
        minValue,
        maxValue,
        allowTime
      );
    } else {
      setError([t('errors.invalidDateTime')]);
    }
    onBlur();
  };

  const onToBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (clickedOnDatetimeCheckbox(e)) {
      return;
    }
    const newToDateTime = parse(displayedToDateTime, inputFormat, new Date());
    if (newToDateTime.getDate()) {
      const fromApiString = getApiStringFromDateAndTime(
        fromDate,
        fromTime,
        allowTime
      );
      const fromDateTime = new Date(fromApiString);
      if (!allowTime) {
        newToDateTime.setHours(...START_OF_DAY_TIME_VALUES);
        fromDateTime.setHours(...START_OF_DAY_TIME_VALUES);
      }
      setError(null);
      checkMinMaxError(
        fromDateTime,
        newToDateTime,
        minValue,
        maxValue,
        allowTime
      );
    } else {
      setError([t('errors.invalidDateTime')]);
    }
    onBlur();
  };

  const onRangeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (clickedOnDatetimeCheckbox(e)) {
      return;
    }
    if (displayedRangeDateTime !== '') {
      const splitted = displayedRangeDateTime.split(' - ', 2);
      if (splitted[0] && splitted[1]) {
        const newFromDateTime = parse(splitted[0], inputFormat, new Date());
        const newToDateTime = parse(splitted[1], inputFormat, new Date());
        if (
          newFromDateTime.getDate() &&
          newToDateTime.getDate() &&
          newFromDateTime <= newToDateTime
        ) {
          setError(null);
          checkMinMaxError(
            newFromDateTime,
            newToDateTime,
            minValue,
            maxValue,
            allowTime
          );
        } else {
          setError([t('errors.invalidBetweenError')]);
        }
      } else {
        setError([t('errors.invalidBetweenError')]);
      }
    }
  };

  const onBlur = () => {
    if (fromDate && toDate && fromDate > toDate) {
      setError([t('errors.invalidBetweenError')]);
    }
  };

  const onCheckboxClick = (checkboxValue: boolean) => {
    setAllowTime(checkboxValue);
    if (value || isColumnFilter) {
      setInputAndApiValue(
        fromDate,
        fromTime,
        toDate,
        toTime,
        checkboxValue,
        isColumnFilter
      );
    } else if (!value && !isColumnFilter && displayedRangeDateTime) {
      setDisaplayedRangeDateTime('');
    }
  };

  const onClickOutside = (e: MouseEvent) => {
    handleFocusOut(e);
    setPopoverOpen(false);
    const fromApiString = getApiStringFromDateAndTime(
      fromDate,
      fromTime,
      allowTime
    );
    const toApiString = getApiStringFromDateAndTime(toDate, toTime, allowTime);
    const dateTimeFrom = new Date(fromApiString);
    const dateTimeTo = new Date(toApiString);
    if (!allowTime) {
      dateTimeFrom.setHours(...START_OF_DAY_TIME_VALUES);
      dateTimeTo.setHours(...START_OF_DAY_TIME_VALUES);
    }
    checkMinMaxError(dateTimeFrom, dateTimeTo, minValue, maxValue, allowTime);
  };

  // Enter commits the picked range, Escape closes without committing; both are swallowed so they don't bubble (e.g. into a parent column-filter Apply handler). Guarded on popoverOpen so the same keys do nothing while only the input is focused.
  const onPopoverKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!popoverOpen) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onSubmit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setPopoverOpen(false);
    }
  };

  const PickerElement = (
    <div className="datetime-picker">
      <DateRangePickerUi
        from={fromDate}
        to={toDate}
        handleRangeSet={handleRangeSet}
        allowRange={true}
        namedRange={undefined}
        defaultValue={null}
        defaultNamedRange={null}
        minValue={minValue}
        maxValue={maxValue}
        autoselectRange
        isDatasetFilter
      />
      {!dateOnly && !forceAllowTime && (
        <div className="datetime-checkbox-container">
          <Checkbox
            value={allowTime}
            onChange={onCheckboxClick}
            label={t('includingTime')}
            checkboxId={DATETIME_CHECKBOX_ID}
          />
        </div>
      )}
      {forceAllowTime && (
        <h3 className="datetime-time-title">{t('timeSectionTitle')}</h3>
      )}
      {allowTime && (
        <div className="timepicker-container range-filter-container">
          <Calendar
            value={fromTime}
            onChange={onFromTimeChange}
            timeOnly
            inline
          />
          <span className="range-dash">-</span>
          <Calendar value={toTime} onChange={onToTimeChange} timeOnly inline />
        </div>
      )}
    </div>
  );

  const InputElement = isColumnFilter ? (
    <>
      <div className="range-filter-container">
        <Input
          name={`${name}_from`}
          value={displayedFromDateTime}
          onChange={manualFromDateTimeChange}
          onKeyDown={onPopoverKeyDown}
          disabled={disabled}
          placeholder={t(
            allowTime ? 'dateTimeInputPlaceholder' : 'dateInputPlaceholder'
          )}
          inputRef={allowTime ? datetimeFromMaskRef : dateFromMaskRef}
          error={error}
          onBlur={onFromBlur}
          onFocus={handleFocusIn}
          hideErrorText
        />
        <span className="range-dash">-</span>
        <Input
          name={`${name}_to`}
          value={displayedToDateTime}
          onChange={manualToDateTimeChange}
          onKeyDown={onPopoverKeyDown}
          disabled={disabled}
          placeholder={t(
            allowTime ? 'dateTimeInputPlaceholder' : 'dateInputPlaceholder'
          )}
          inputRef={allowTime ? datetimeToMaskRef : dateToMaskRef}
          error={error}
          onBlur={onToBlur}
          onFocus={handleFocusIn}
          hideErrorText
        />
      </div>
      <InputErrorMessage error={error} className="datetime-error" />
    </>
  ) : (
    <Input
      name={`${name}_range`}
      value={displayedRangeDateTime}
      onChange={manualRangeDateTimeChange}
      onKeyDown={onPopoverKeyDown}
      disabled={disabled}
      placeholder={
        allowTime
          ? `${t('dateTimeInputPlaceholder')} - ${t('dateTimeInputPlaceholder')}`
          : `${t('dateInputPlaceholder')} - ${t('dateInputPlaceholder')}`
      }
      inputRef={allowTime ? datetimeRangeMaskRef : dateRangeMaskRef}
      error={error}
      onBlur={onRangeBlur}
      onFocus={handleFocusIn}
      onClick={() => setPopoverOpen(true)}
      hideErrorText
    />
  );

  return isColumnFilter ? (
    <div
      className="date-filter-column-container"
      onFocus={handleFocusIn}
      onBlur={handleFocusOut}
    >
      {PickerElement}
      {InputElement}
    </div>
  ) : (
    <>
      <Popover
        isOpen={popoverOpen}
        positions={['bottom', 'top', 'left', 'right']}
        onClickOutside={onClickOutside}
        align="start"
        content={
          <div
            className="datetime-popover"
            ref={popoverRef}
            onFocus={handleFocusIn}
            onBlur={handleFocusOut}
          >
            {PickerElement}
            <div className="datetime-picker-footer">
              <Button onClick={onSubmit} fill="clear" color="secondary">
                {t('use')}
              </Button>
            </div>
          </div>
        }
      >
        <div
          className="datetime-picker-container"
          onFocus={handleFocusIn}
          onBlur={handleFocusOut}
        >
          <Button
            fill="clear"
            color="secondary"
            disabled={disabled}
            onClick={() => setPopoverOpen(true)}
          >
            <IonIcon icon={calendarOutline} />
          </Button>
          {InputElement}
        </div>
      </Popover>
      <InputErrorMessage error={error} className="datetime-error" />
    </>
  );
};

export default DateTimeRangePicker;
